import { useEffect, useState } from "react";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Loader2, ListTodo } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { invalidateTaskMessageAttachments } from "@/hooks/useTaskMessageAttachments";
import { useWorkspaceProjects, useProjectColumns } from "@/hooks/useWorkspaceProjects";
import { useProjectTasksPicker } from "@/hooks/useProjectTasksPicker";
import { supabase } from "@/integrations/supabase/client";

export interface AttachMessageInfo {
  id: string;
  content: string;
  role?: "user" | "assistant";
  created_at?: string;
}

interface AttachMessageToTaskDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  message: AttachMessageInfo | null;
  workspaceId: string;
  memberId: string | null;
}

const COLUMN_ALL = "__all__";

export function AttachMessageToTaskDialog({
  open,
  onOpenChange,
  message,
  workspaceId,
  memberId,
}: AttachMessageToTaskDialogProps) {
  const [projectId, setProjectId] = useState("");
  const [columnId, setColumnId] = useState<string>(COLUMN_ALL);
  const [taskId, setTaskId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();
  const { projects, loading: projectsLoading } = useWorkspaceProjects(
    open ? workspaceId : null,
  );
  const { columns, loading: columnsLoading } = useProjectColumns(projectId || null);
  const effectiveColumnId = columnId === COLUMN_ALL ? null : columnId;
  const { options, loading: tasksLoading } = useProjectTasksPicker({
    workspaceId,
    projectId: projectId || null,
    columnId: effectiveColumnId,
    enabled: open && Boolean(projectId),
  });

  useEffect(() => {
    if (!open) return;
    setProjectId(projects[0]?.id ?? "");
    setColumnId(COLUMN_ALL);
    setTaskId("");
  }, [open, projects]);

  useEffect(() => {
    if (!open) return;
    setColumnId(COLUMN_ALL);
    setTaskId("");
  }, [open, projectId]);

  useEffect(() => {
    if (!open || tasksLoading) return;
    if (options.length === 0) {
      setTaskId("");
      return;
    }
    setTaskId((current) =>
      current && options.some((o) => o.id === current) ? current : options[0].id,
    );
  }, [open, options, tasksLoading]);

  const preview = message?.content?.trim()
    ? message.content.length > 120
      ? `${message.content.slice(0, 120)}…`
      : message.content
    : "(sem texto)";

  const handleSubmit = async () => {
    if (!message?.id || !taskId) return;

    try {
      setSubmitting(true);
      const { error } = await supabase.from("workspace_task_message_attachments").insert({
        task_id: taskId,
        message_id: message.id,
        workspace_id: workspaceId,
        created_by_member_id: memberId,
      });

      if (error) {
        if (error.code === "23505") {
          throw new Error("Esta mensagem já está anexada a esta tarefa.");
        }
        throw error;
      }

      invalidateTaskMessageAttachments(taskId);
      onOpenChange(false);
      toast({
        title: "Mensagem anexada",
        description: "A mensagem foi vinculada à tarefa selecionada.",
      });
    } catch (err: unknown) {
      toast({
        title: "Erro ao anexar mensagem",
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md min-w-0 overflow-hidden sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ListTodo className="h-5 w-5" />
            Anexar a tarefa
          </DialogTitle>
        </DialogHeader>

        <div className="min-w-0 space-y-4 py-2">
          {message && (
            <div className="rounded-lg border bg-muted/40 p-3">
              <p className="mb-1 text-xs font-medium text-muted-foreground">Mensagem</p>
              <p className="line-clamp-3 whitespace-pre-wrap text-sm">{preview}</p>
              {message.created_at && (
                <p className="mt-2 text-xs text-muted-foreground">
                  {format(parseISO(message.created_at), "dd/MM/yyyy 'às' HH:mm", {
                    locale: ptBR,
                  })}
                </p>
              )}
            </div>
          )}

          {projectsLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Carregando projetos...
            </div>
          ) : projects.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Nenhum projeto disponível. Crie um projeto em Tarefas antes de anexar.
            </p>
          ) : (
            <>
              <div className="min-w-0 space-y-2">
                <Label>Projeto</Label>
                <Select value={projectId} onValueChange={setProjectId}>
                  <SelectTrigger className="w-full max-w-full">
                    <SelectValue placeholder="Selecionar projeto" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {projectId && (
                <div className="min-w-0 space-y-2">
                  <Label>Coluna (opcional)</Label>
                  {columnsLoading ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Carregando colunas...
                    </div>
                  ) : (
                    <Select value={columnId} onValueChange={setColumnId}>
                      <SelectTrigger className="w-full max-w-full">
                        <SelectValue placeholder="Todas as colunas" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={COLUMN_ALL}>Todas as colunas</SelectItem>
                        {columns.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}

              {projectId && (
                <div className="min-w-0 space-y-2">
                  <Label>Tarefa ou subtarefa</Label>
                  {tasksLoading ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Carregando tarefas...
                    </div>
                  ) : options.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      Nenhuma tarefa encontrada neste projeto
                      {effectiveColumnId ? " para a coluna selecionada" : ""}.
                    </p>
                  ) : (
                    <Select value={taskId} onValueChange={setTaskId}>
                      <SelectTrigger className="w-full max-w-full">
                        <SelectValue placeholder="Selecionar tarefa" />
                      </SelectTrigger>
                      <SelectContent>
                        {options.map((opt) => (
                          <SelectItem key={opt.id} value={opt.id}>
                            <span className={opt.isSubtask ? "pl-1" : ""}>
                              {opt.isSubtask ? "↳ " : ""}
                              {opt.title}
                              {opt.isSubtask && opt.parentTitle
                                ? ` (${opt.parentTitle})`
                                : ""}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="flex justify-end">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            onClick={() => void handleSubmit()}
            disabled={
              submitting ||
              !message?.id ||
              projects.length === 0 ||
              !projectId ||
              !taskId ||
              tasksLoading
            }
          >
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Anexar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
